/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useProviderForm } from '../hooks';

import * as services from '../../../../client/services';

import { ReturnComponent } from '.';
import { Alert, AutoComplete } from '../../../element';
import { useTranslation } from '../../../../i18n';
import { resetError } from '../../../../entities/providers';

const lookUpCities = (city: string) => (query: string, cb: any) => {
  services
    .getAlfredDepartments({
      city,
      address: query ? query.trim() : '',
    })
    .then((list) => {
      const mapped = list.map((department) => {
        const value = `${department.name}, ${department.address}, ${department.city.name}`;

        return { ...department, value };
      });

      cb(mapped);
    })
    .catch(() => {
      cb([]);
    });
};

const DepartmentInfo: React.FC<{ department: services.Department }> = ({ department }) => {
  const { address, post_code, name } = department;

  return (
    <div className="mt-20">
      {address && post_code && (
        <Alert
          closable={false}
          title={name}
          description={`${address}, ${post_code}`}
          type="info"
          showIcon
          className="is-light mb-20"
        />
      )}
    </div>
  );
};

const Field: ReturnComponent = ({ type }) => {
  const [t] = useTranslation('rates');

  const dispatch = useDispatch();

  const cityProps = useProviderForm('city', type);
  const postCodeProps = useProviderForm('postCode', type);
  const departmentProp = useProviderForm('department', type);

  const { onChange, value } = useProviderForm('departments', type);

  const [department, setDepartment] = useState(value || '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const div = ref.current;

    const inputRef = {
      input: null as HTMLInputElement | null,
    };

    function handleChange(event: Event) {
      const val = (event as any).target.value;

      setDepartment(val);
      onChange(val);

      if (!val) {
        departmentProp.onChange(null);
      }

      dispatch(resetError({ key: `${type}_departmentsAutoComplete` }));
    }

    const setListener = () => {
      inputRef.input = (div && div.querySelector('input')) as any;

      if (inputRef.input) {
        inputRef.input.addEventListener('input', handleChange);
        inputRef.input.addEventListener('blur', handleChange);
      } else {
        setTimeout(setListener, 500);
      }
    };

    setListener();

    return () => {
      if (inputRef.input) {
        inputRef.input.removeEventListener('input', handleChange);
        inputRef.input.removeEventListener('blur', handleChange);
      }
    };
  }, []);

  useEffect(() => {
    setDepartment(value);
  }, [value]);

  useEffect(() => {
    departmentProp.onChange(null);
    onChange('');
    setDepartment('');
  }, [cityProps.value, departmentProp.onChange, onChange]);

  const handleSelect = useCallback(
    (item: services.Department) => {
      setDepartment(item.name);

      departmentProp.onChange(item);
      postCodeProps.onChange(item.post_code);
    },
    [type, departmentProp, setDepartment]
  );

  return (
    <>
      <div ref={ref}>
        <AutoComplete
          value={department}
          disabled={!cityProps.value}
          placeholder={t('rates.form.department')}
          fetchSuggestions={lookUpCities(cityProps.value)}
          onSelect={handleSelect}
          className="full-width"
        />
      </div>

      {departmentProp.value && <DepartmentInfo department={(departmentProp.value as never) as services.Department} />}
    </>
  );
};

export default Field;
